import React, { useState, useEffect } from "react";
import { ConfirmDeleteModal } from "../../../../../../Components/ConfirmDeleteModal";
import {
  TableColumnsType,
  Button,
  Space,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  Divider,
  message,
  Spin,
  Pagination,
  Typography,
} from "antd";
import { Icon } from "@iconify/react";
import * as Yup from "yup";
import { Formik } from "formik";
import { debounce } from "lodash";

import { DataTable } from "../../../../../../Components/DataTable/DataTable";
import { panelApiUrl } from "~/Utils";
import { api, remove } from "../../../../../../../SPA/Services/api.service";
import { getFormattedDate, getKey } from "@utils/CommonUtils";
import { updatedAtOf, updatedByOf } from "../../../../../../Utils/auditFields";
import { apiSortField } from "../../../../../../Utils/sortField";
import styles from "@styles/variables.module.scss";
import { RegionDrilldownModal } from "./RegionDrilldownModal";



export const RegionList = () => {
  const [regionData, setRegionData] = useState<any[]>([]);
  /* The list URL, re-requested page by page by DataTable's Export (as VendorList). */
  const [regionExportUrl, setRegionExportUrl] = useState<string>("");
  const [regionLoading, setRegionLoading] = useState(false);
  const [regionError, setRegionError] = useState<string | null>(null);
  const [editingRegion, setEditingRegion] = useState<any>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [deletingRegionId, setDeletingRegionId] = useState<string | null>(null);
  const [deleteRegionModal, setDeleteRegionModal] = useState(false);
  const [deleteRegionLoading, setDeleteRegionLoading] = useState(false);
  const [openRegionModal, setOpenRegionModal] = useState(false);
  const [regionTotal, setRegionTotal] = useState<number>(0);
  const [regionPage, setRegionPage] = useState<number>(0);
  const [regionSearch, setRegionSearch] = useState<string>("");
  const [clearRegionSearch, setClearRegionSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [drilldownRegion, setDrilldownRegion] = useState<any>(null);
  const [existingRegions, setExistingRegions] = useState<any[]>([]);

  useEffect(() => {
    if (openRegionModal) {
      api
        .get(`${panelApiUrl}/region/get-region-list?page=0&size=1000`)
        .then((res: any) => {
          const records = res?.data?.response?.data?.records || [];
          setExistingRegions(records);
        })
        .catch(() => setExistingRegions([]));
    }
  }, [openRegionModal]);

  const regionSchema = Yup.object().shape({
    regionName: Yup.string()
      .required("Please enter a region")
      .min(2, "Region must be at least 2 characters long")
      .max(50, "Region must not exceed 50 characters")
      .trim("Leading and trailing spaces are not allowed")
      .strict(true)
      .test("unique", "This name already exists.", function (value) {
        if (!value) return true;
        const isDuplicate = existingRegions.some(
          (r: any) => r.regionName?.toLowerCase() === value.toLowerCase() && r.id !== editingRegion?.id
        );
        return !isDuplicate;
      }),
  });

  const recordsPerPage = 10;

  useEffect(() => {
    fetchRegionData(regionSearch, regionPage, sortOrder, orderBy);
  }, []);

  const handleRegionSearchChange = debounce((value: string, isLoading: boolean) => {
    setClearRegionSearch(false);
    setRegionSearch(value);
    setRegionPage(0);
    setRegionLoading(isLoading);
    fetchRegionData(value, 0, sortOrder, orderBy);
  }, 1000);

  const handleTableSort = (column: any, currentSortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (currentSortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (currentSortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      } else {
        setSortOrder("desc");
        order = "desc";
      }
    }
    setOrderBy(column.dataIndex);
    setRegionPage(0);
    fetchRegionData(regionSearch, 0, order, column.dataIndex);
  };

  const fetchRegionData = (searchText: string = "", currentPage: number = 0, currentOrder: string = sortOrder, currentOrderBy: string = orderBy) => {
    setRegionLoading(true);
    setRegionError(null);

    const size = recordsPerPage;
    const url = `${panelApiUrl}/region/get-region-list?page=${currentPage}&size=${size}&searchText=${encodeURIComponent(searchText)}&order=${currentOrder}&orderBy=${apiSortField(currentOrderBy)}`;
    setRegionExportUrl(url);

    api
      .get(url)
      .then((res: any) => {
        const responseData = res?.data?.response?.data;
        const records = responseData?.records || [];
        setRegionTotal(responseData?.totalCount || 0);

        const mappedData = records.map((item: any, index: any) => ({
          key: getKey(currentPage, index),
          id: item.id,
          regionName: item.regionName || "-",
          categoryCount: item.categoryCount || 0,
          vendorCount: item.vendorCount || 0,
          subRegionCount: item.subRegionCount || 0,
          createdAt: getFormattedDate(item.createdOn) || "-",
          createdBy: item.createdBy || "-",
          updatedAt: updatedAtOf(item),
          updatedBy: updatedByOf(item),
          ...item,
        }));

        setRegionData(mappedData);
      })
      .catch((err: any) => {
        console.error("Error fetching region data:", err);
        setRegionData([]);
        setRegionTotal(0);
        setRegionError("Failed to fetch region data");
        message.error("Error fetching region data");
      })
      .finally(() => {
        setRegionLoading(false);
      });
  };

  const handleRegionMenuClick = (key: string, record: { [key in string]: any }) => {
    if (key === "edit") {
      setEditingRegion(record);
      setModalMode("edit");
      setOpenRegionModal(true);
    } else if (key === "delete") {
      setDeletingRegionId(record?.id);
      setDeleteRegionModal(true);
    }
  };

  const handleDeleteRegion = () => {
    setDeleteRegionLoading(true);
    remove(`${panelApiUrl}/region/${deletingRegionId}`)
      .then((res: any) => {
        setDeleteRegionLoading(false);
        message.success(res?.data?.message || "Region deleted successfully");
        setDeleteRegionModal(false);
        setDeletingRegionId(null);
        fetchRegionData(regionSearch, regionPage, sortOrder, orderBy);
      })
      .catch((err: any) => {
        setDeleteRegionLoading(false);
        message.error(err?.data?.message || "Failed to delete region");
        setDeleteRegionModal(false);
        setDeletingRegionId(null);
      });
  };

  const handleCloseRegionModal = () => {
    setOpenRegionModal(false);
    setEditingRegion(null);
  };

  const regionColumns: TableColumnsType = [
    { 
      title: "Region Name", 
      dataIndex: "regionName", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "regionName" ? "ascend" : sortOrder === "desc" && orderBy === "regionName" ? "descend" : undefined,
    },
    {
      title: "Category Count",
      dataIndex: "categoryCount",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "categoryCount" ? "ascend" : sortOrder === "desc" && orderBy === "categoryCount" ? "descend" : undefined,
    },
    { 
      title: "Vendor Count", 
      dataIndex: "vendorCount", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "vendorCount" ? "ascend" : sortOrder === "desc" && orderBy === "vendorCount" ? "descend" : undefined,
    },
    { 
      title: "Sub Region Count", 
      dataIndex: "subRegionCount", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "subRegionCount" ? "ascend" : sortOrder === "desc" && orderBy === "subRegionCount" ? "descend" : undefined,
    },
    { 
      title: "Created At", 
      dataIndex: "createdOn", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdOn" ? "ascend" : sortOrder === "desc" && orderBy === "createdOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    { 
      title: "Created By", 
      dataIndex: "createdBy", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdBy" ? "ascend" : sortOrder === "desc" && orderBy === "createdBy" ? "descend" : undefined,
    },
    { 
      title: "Updated At", 
      dataIndex: "updatedOn", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedOn" ? "ascend" : sortOrder === "desc" && orderBy === "updatedOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    { 
      title: "Updated By", 
      dataIndex: "updatedBy", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedBy" ? "ascend" : sortOrder === "desc" && orderBy === "updatedBy" ? "descend" : undefined,
    },
    {
      title: "Action",
      dataIndex: " ",
      width: 90,
      render: (value: any, record: any) => {
        const menuItems = [
          {
            label: (
              <Typography.Text
                type="secondary"
                style={{ color: styles?.primary }}
              >
                <Space>
                  <Icon icon="akar-icons:edit" inline fontSize={16} style={{ color: styles?.primary }} />
                  Edit
                </Space>
              </Typography.Text>
            ),
            key: "edit",
          },
          {
            label: (
              <Typography.Text
                type="danger"
              >
                <Space>
                  <Icon icon="fluent:delete-48-regular" inline fontSize={16} />
                  Delete
                </Space>
              </Typography.Text>
            ),
            key: "delete",
          },
        ];
        return (
          <Dropdown
            overlay={
              <Menu
                /* A portalled menu bubbles up the React tree, so the row
                   click handler on the cell would fire too. */
                onClick={({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleRegionMenuClick(key, record);
                }}
                items={menuItems}
              />
            }
            trigger={["click"]}
            placement="bottomLeft"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <Icon icon={"bi:three-dots-vertical"} style={{ cursor: "pointer" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      {regionLoading && regionData.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : regionError ? (
        <Typography.Text type="danger">{regionError}</Typography.Text>
      ) : (
        <>
          <DataTable
            multiSelect={true}
            hideExport={false}
            /* The three-dots Action column is a menu, not data. */
            excludeExportCols={["Action"]}
            exportFileName="Region"
            exportApiUrl={regionExportUrl}
            columns={regionColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={regionData}
            className="table-clickable"
            /* A region opens its drill-down: categories, sub-regions, vendors. */
            onClick={(record: any) => setDrilldownRegion(record)}
            searchPlaceholderText="Search by region name"
            searchQuery={regionSearch}
            clearStringSearch={clearRegionSearch}
            onSearchChange={handleRegionSearchChange}
            inputDisable={regionLoading}
            isPagination={true}
            isServerSearchApi={true}
            totalRecords={regionTotal}
            recordsPerPage={recordsPerPage}
            additionalCTAToRight={true}
            additionalCTA={
              <Button
                type="primary"
                onClick={() => {
                  setEditingRegion(null);
                  setModalMode("add");
                  setOpenRegionModal(true);
                }}
              >
                <Space>
                  <Icon icon="akar-icons:plus" inline />
                  Add
                </Space>
              </Button>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={regionPage + 1}
              total={regionTotal}
              onChange={(newPage) => {
                const startIndex = newPage - 1;
                setRegionPage(startIndex);
                fetchRegionData(regionSearch, startIndex, sortOrder, orderBy);
              }}
              showSizeChanger={false}
              pageSize={10}
            />
          </div>
        </>
      )}

      {/* Add / Edit / View Region Modal */}
      <Modal
        title={modalMode === "view" ? "View Region" : editingRegion ? "Edit Region" : "Add Region"}
        open={openRegionModal}
        maskClosable={false}
        centered
        width="30%"
        onCancel={handleCloseRegionModal}
        footer={null}
        destroyOnClose={true}
      >
        <Divider style={{ margin: "-20px 0 22px", borderTop: "1px solid #d9d9d9" }} />

        <Formik
          key={editingRegion?.id || "add"}
          enableReinitialize={true}
          initialValues={{
            regionName: editingRegion?.regionName || "",
          }}
          validationSchema={regionSchema}
          validateOnMount
          onSubmit={(values, { resetForm }) => {
            setIsLoading(true);

            if (editingRegion) {
              api
                .put(`${panelApiUrl}/region/${editingRegion.id}`, values)
                .then((res: any) => {
                  message.success(res?.data?.message || "Region updated successfully");
                  setIsLoading(false);
                  resetForm();
                  handleCloseRegionModal();
                  fetchRegionData(regionSearch, regionPage, sortOrder, orderBy);
                })
                .catch((err: any) => {
                  message.error(err?.data?.message || "Failed to update region");
                  setIsLoading(false);
                });
            } else {
              api
                .post(`${panelApiUrl}/region`, values)
                .then((res: any) => {
                  message.success(res?.data?.message);
                  setIsLoading(false);
                  resetForm();
                  handleCloseRegionModal();
                  fetchRegionData(regionSearch, regionPage, sortOrder, orderBy);
                })
                .catch((err: any) => {
                  message.error(err?.data?.message || "Failed to add region");
                  setIsLoading(false);
                });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, dirty }) => (
            <Form layout="vertical" onFinish={handleSubmit}>
              <Form.Item required
                label="Region Name"
                validateStatus={touched.regionName && errors.regionName ? "error" : ""}
                help={touched.regionName && typeof errors.regionName === "string" ? errors.regionName : undefined}
              >
                <Input
                  name="regionName"
                  value={values.regionName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter region name"
                  maxLength={50}
                  disabled={modalMode === "view"}
                />
              </Form.Item>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginTop: 32 }}>
                <Button onClick={handleCloseRegionModal} disabled={isLoading}>
                  {modalMode === "view" ? "Close" : "Cancel"}
                </Button>

                {modalMode !== "view" && (
                  <Button type="primary" htmlType="submit" disabled={!dirty || !isValid} loading={isLoading}>
                    {editingRegion ? "Update" : "Add"}
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      <RegionDrilldownModal
        region={drilldownRegion}
        onClose={() => setDrilldownRegion(null)}
      />

      {/* Delete Region Modal */}
      <ConfirmDeleteModal
        open={deleteRegionModal}
        message="Are you sure you want to delete the region?"
        isConfirming={deleteRegionLoading}
        onCancel={() => {
          setDeleteRegionModal(false);
          setDeletingRegionId(null);
        }}
        onConfirm={handleDeleteRegion}
      />
    </>
  );
};
